import React, { useEffect, useState } from 'react'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { useIntl } from 'react-intl'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { ColumnDef, ColumnFiltersState, FilterFn, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { Pagination, Table } from 'react-bootstrap'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import NoDataPage from '../../widgets/components/NoDataPage'
import UpdateScoreModal from './UpdateScoreModal'

declare module '@tanstack/react-table' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
}

interface MyCompaniesTable {
    id: number
    queue: number
    name: string
    company: string
    invitees: string
    successfulInvitees: string
    country: string
    requestDate: string
    fileType: string
    storageType: string
    ownedBy: string
    industry: string

}


const AdminAppDataTable = () => {
    const { formatMessage } = useIntl()
    const [loading, setLoading] = useState(false) //eslint-disable-line
    const [isActive, setIsActive] = useState(false)
    const [shareholders, setShareholders] = useState<MyCompaniesTable[]>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    })
    const [search, setSearch] = useState('') //eslint-disable-line
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch('https://dummyjson.com/c/12ce-2a2e-4d66-960d')
                const data = await response.json()
                setShareholders(data)  // Assuming the response is an array of shareholders
            } catch (error) {
                console.error('Error fetching data:', error)
            }
        }

        fetchData()
    }, [])

    const columns = React.useMemo<ColumnDef<MyCompaniesTable, any>[]>(() => [
        {
            accessorKey: 'fileName',
            id: 'fileName',
            header: 'File Name',
            cell: (info) => {
                return (
                    <div className='d-flex align-items-center gap-2' >
                        <img src={toAbsoluteUrl('/media/icons/file_extensions/pdf.svg')} alt="PDF" />
                        <div>
                            <h5 className='m-0 fw-normal font-size-13'>{info.getValue()}</h5>
                        </div>
                    </div >
                )
            },
            filterFn: 'includesString',
        },
        {
            accessorKey: 'fileSize',
            id: 'fileSize',
            header: 'File Size',
            cell: (info) => info.getValue(),
            filterFn: 'includesString',
        },
        {
            accessorKey: 'status',
            id: 'status',
            header: 'Status',
            cell: (info) => info.getValue(),
            filterFn: 'includesString',
        },
        {
            accessorKey: 'outputScore',
            id: 'outputScore',
            header: 'Output Score',
            cell: (info) => {
                return (
                    <div className='d-flex align-items-center gap-2 w-100' >
                        <div className='w-100'>
                            <p className='m-0 fw-normal font-size-10 text-primary'>{info.getValue()}</p>
                            <div className='w-100'>
                                <div className='h-10px progress'>
                                    <div
                                        className='progress-bar bg-primary'
                                        role='progressbar'
                                        style={{
                                            width: `${info.getValue()}%`,
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div >
                )
            },
            filterFn: 'includesString',
        },
        {
            accessorKey: 'scoreGeneratedOn',
            id: 'scoreGeneratedOn',
            header: 'Score Generated On',
            cell: (info) => info.getValue(),
            filterFn: 'includesString',
        },
        {
            accessorKey: 'fileProcessedOn',
            id: 'fileProcessedOn',
            header: 'File Processed On',
            cell: (info) => info.getValue(),
            filterFn: 'includesString',
        },
    ], [])

    const table = useReactTable({
        data: shareholders,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            columnFilters,
            globalFilter,
            pagination,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        globalFilterFn: 'fuzzy',
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    })
    const pageCount = Math.ceil(table.getPrePaginationRowModel().rows.length / pagination.pageSize) //eslint-disable-line
    const currentPage = table.getState().pagination.pageIndex //eslint-disable-line

    const handlePageChange = (pageIndex: number) => { //eslint-disable-line
        table.setPageIndex(pageIndex)
    }
    return (
        <>
            <div className='d-flex flex-md-row flex-column align-items-md-center gap-2 mb-4'>
                <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center align-items-start w-100 gap-md-1 gap-2 '>
                    <h1 className='mb-0 font-size-16'>{formatMessage({ id: 'My Companies' })}</h1>
                    {/* <div className='h-100 border bg-white d-flex rounded'>
                            <button
                                className={clsx(
                                    'text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36'
                                )}
                                id='fc-toogle'
                                title='Sort and Filter'
                                data-bs-toggle='tooltip'
                                data-bs-placement='left'
                                data-bs-dismiss='click'
                                data-bs-trigger='hover'
                                onClick={() => {
                                }}
                            >
                                <KTSVG
                                    path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                                    className='svg-icon-6 svg-icon-muted me-1'
                                />
                                <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span> */}
                    {/* {selectedShareholderTypeFilter || selectedShareholderSortTypeFilter ? '*' : ''} */}
                    {/* </button> */}
                    {/* </div> */}
                    <form className='align-items-center w-100 w-md-auto d-flex'>
                        <span className='svg-icon svg-icon-3 position-absolute ms-3'>
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                className='mh-50px'
                            >
                                <rect
                                    opacity='0.5'
                                    x='17.0365'
                                    y='15.1223'
                                    width='8.15546'
                                    height='2'
                                    rx='1'
                                    transform='rotate(45 17.0365 15.1223)'
                                    fill='currentColor'
                                ></rect>
                                <path
                                    d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                                    fill='currentColor'
                                ></path>
                            </svg>
                        </span>
                        <input
                            className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder='Search Files'
                        />
                    </form>
                </div>
                <div className='d-flex gap-md-1 gap-1 flex-shrink-0'>
                    <button
                        className='btn text-white custom-button-property bg-primary height-36 d-flex align-items-center font-size-12 w-100 justify-content-center '
                    // onClick={() => {
                    //     setIsActive(true)
                    // }}
                    >
                        {formatMessage({ id: 'Upload Files' })}
                    </button>
                </div>
            </div>
            <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
                {!loading && table.getRowModel().rows.length === 0 ? (
                    <div className='my-5 mx-6'>
                        <NoDataPage />
                    </div>
                ) : (
                    <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
                        <thead className='custom-thead border-bottom'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    key={headerGroup.id}
                                    className='font-size-12 font-weight-400 table-row border-dark-mode'
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className='min-w-120px pb-6 pt-0 px-4'
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {{
                                                        asc: ' ▲',
                                                        desc: ' ▼',
                                                    }[header.column.getIsSorted() as string] ?? null}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                    <th className='min-w-120px pb-6 pt-4 text-end px-4'>{formatMessage({ id: 'Actions' })}</th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {loading ? (
                                <>
                                    <SkeletonRow count={8} />
                                </>
                            ) : (
                                table.getRowModel().rows.map((row, index) => (
                                    <React.Fragment key={row.id}>
                                        <tr
                                            key={row.id}
                                            className='border-bottom'
                                        >
                                            {row.getVisibleCells().map((cell, cellIndex) => (
                                                <>
                                                    {cellIndex === 0 ? (
                                                        <td key={cell.id} className='pt-4 px-4 border-0' style={{ minWidth: '200px' }}>
                                                            <div className='d-flex align-items-center gap-2'>
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </div>
                                                        </td>
                                                    ) :
                                                        cellIndex === 2 ? (
                                                            <td key={cell.id} className='pt-5 px-4 border-0'>
                                                                <div className={`d-flex justify-content-center align-items-center gap-2 badge bg-success bg-opacity-25 text-success py-2`} style={{ width: '120px' }}>
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </div>
                                                            </td>
                                                        ) : cellIndex === 3 ? (
                                                            <td key={cell.id} className='  pt-5 px-4 border-0' style={{ minWidth: '200px' }}>
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>
                                                        ) : (
                                                            <td
                                                                key={cell.id}
                                                                className={`pt-5 px-4 border-0 min-w-120px`}
                                                            >
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>
                                                        )}
                                                </>
                                            ))}
                                            <td
                                                className='pt-3 px-4 text-end'
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    // setMemoryId(row.original.memoryId)
                                                }}
                                            >
                                                <div className='text-success d-flex align-items-center gap-2 justify-content-end'>
                                                    <img
                                                        src={toAbsoluteUrl('/media/icons/duotune/general/ic-file.svg')} alt="file" className='cursor-pointer' />
                                                    <img
                                                        src={toAbsoluteUrl('/media/icons/duotune/general/ic-download.svg')} alt="download" className='cursor-pointer' />
                                                    <img
                                                        src={toAbsoluteUrl('/media/icons/duotune/general/ic-edit.svg')} alt="edit" className='cursor-pointer' onClick={() => setIsActive(true)} />
                                                </div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                        <UpdateScoreModal isActive={isActive} setIsActive={setIsActive} />
                    </Table>
                )}
            </div>
            <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom mb-8'>
                <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4 mb-md-0'>
                    <select
                        aria-label='Default select example'
                        className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark '
                        style={{ outline: 'none' }}
                        value={pagination.pageSize}
                        onChange={(e) => {
                            const newSize = Number(e.target.value)
                            setPagination((prev) => ({
                                ...prev,
                                pageSize: newSize,
                                pageIndex: 0, // Reset to first page when page size changes
                            }))
                        }}
                    >
                        {[10, 20, 50, 100].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize}
                            </option>
                        ))}
                    </select>

                    <span className='font-size-13 text-center text-md-start'>
                        {formatMessage({
                            id: `Showing ${currentPage * pagination.pageSize + 1} to ${Math.min(
                                (currentPage + 1) * pagination.pageSize,
                                table.getPrePaginationRowModel().rows.length
                            )} of ${table.getPrePaginationRowModel().rows.length} records`,
                        })}
                    </span>
                </div>

                {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
                        {'<<'}
                    </Pagination.First>

                    <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                    >
                        {'<'}
                    </Pagination.Prev>

                    {Array.from({ length: pageCount }, (_, i) => (
                        <Pagination.Item
                            key={i}
                            active={i === currentPage}
                            onClick={() => handlePageChange(i)}
                        >
                            {i + 1}
                        </Pagination.Item>
                    ))}

                    <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= pageCount - 1}
                    >
                        {'>'}
                    </Pagination.Next>

                    <Pagination.Last
                        onClick={() => handlePageChange(pageCount - 1)}
                        disabled={currentPage >= pageCount - 1}
                    >
                        {'>>'}
                    </Pagination.Last>
                </Pagination> */}
                <Pagination className="w-md-100 w-auto d-md-flex flex-md-end">
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
                        {'<<'}
                    </Pagination.First>

                    <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                    >
                        {'<'}
                    </Pagination.Prev>

                    {(() => {
                        const items = [];
                        const totalPages = pageCount;
                        const current = currentPage;
                        const pageNeighbours = 1; // adjust how many neighbors you want

                        const startPage = Math.max(1, current - pageNeighbours);
                        const endPage = Math.min(totalPages - 2, current + pageNeighbours);

                        // Always show first page
                        items.push(
                            <Pagination.Item
                                key={0}
                                active={current === 0}
                                onClick={() => handlePageChange(0)}
                            >
                                1
                            </Pagination.Item>
                        );

                        // Show start ellipsis
                        if (startPage > 1) {
                            items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
                        }

                        // Render middle page numbers
                        for (let i = startPage; i <= endPage; i++) {
                            items.push(
                                <Pagination.Item
                                    key={i}
                                    active={i === current}
                                    onClick={() => handlePageChange(i)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            );
                        }

                        // Show end ellipsis
                        if (endPage < totalPages - 2) {
                            items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
                        }

                        // Always show last page
                        if (totalPages > 1) {
                            items.push(
                                <Pagination.Item
                                    key={totalPages - 1}
                                    active={current === totalPages - 1}
                                    onClick={() => handlePageChange(totalPages - 1)}
                                >
                                    {totalPages}
                                </Pagination.Item>
                            );
                        }

                        return items;
                    })()}

                    <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= pageCount - 1}
                    >
                        {'>'}
                    </Pagination.Next>

                    <Pagination.Last
                        onClick={() => handlePageChange(pageCount - 1)}
                        disabled={currentPage >= pageCount - 1}
                    >
                        {'>>'}
                    </Pagination.Last>
                </Pagination>

            </div>
        </>
    )
}

export default AdminAppDataTable
